import {
    CalendarDate,
    CalendarDateTime,
    DayOfWeek,
    getCalendarDateTime,
    RecurrencePattern,
    TaskParentId,
    TaskTypeId,
    UserId,
} from "@sp-crm/core";
import { UserSelect } from "components/shared/user-select";
import { CalendarDateTimeInput } from "components/ui/calendar-date-time";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import { UpdateTaskPayload } from "generated/graphql";
import { usePreventUnload } from "hooks/use-prevent-unload";
import React, { useCallback, useMemo } from "react";
import { Link, Prompt } from "react-router-dom";
import {
    allTaskTypesSentinel,
    FilterableTaskTypes,
    unsetTaskTypeSentinel,
} from "store/slices/tasks-dashboard";
import { RecurrenceConfig } from "./recurrence-config";
import { TaskTypeSelect } from "./task-type-select";
import { Taskv2 } from "./types";

interface TaskFormV2Props {
    initial: Taskv2;
    mode: "update" | "create";
    onClose: () => void;
    saveTask: (parentId: TaskParentId, payload: UpdateTaskPayload) => Promise<void>;
    entityLinkDisabled?: boolean;
    onDirtyChange?: (isDirty: boolean) => void;
}

export const TaskFormV2: React.FC<TaskFormV2Props> = props => {
    const { task, entity } = props.initial;
    const { onClose, saveTask, mode, onDirtyChange } = props;
    const [changes, setChanges] = React.useState<UpdateTaskPayload>({});
    const [updating, setUpdating] = React.useState(false);
    const isDirty = Object.keys(changes).length > 0;
    usePreventUnload(isDirty);
    React.useEffect(() => {
        onDirtyChange?.(isDirty);
        return () => onDirtyChange?.(false);
    }, [isDirty, onDirtyChange]);

    const handleCancel = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setChanges({});
            onClose();
        },
        [onClose, setChanges],
    );

    const handleSave = useCallback(
        async (e: React.SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const saveParams: UpdateTaskPayload =
                mode === "create"
                    ? {
                          completedAt: task.completedAt,
                          dueDate: task.dueDate,
                          dueDateTime: task.dueDateTime,
                          ownerId: task.ownerId,
                          taskTypeId: task.taskTypeId,
                          status: task.status,
                          text: task.text,
                          notes: task.notes,
                          recurrencePattern: task.recurrencePattern as RecurrencePattern,
                          recurrenceInterval: task.recurrenceInterval,
                          recurrenceDaysOfWeek: task.recurrenceDaysOfWeek as DayOfWeek[],
                          recurrenceDayOfMonth: task.recurrenceDayOfMonth,
                          ...changes,
                      }
                    : changes;

            if (Object.keys(saveParams).length > 0) {
                setUpdating(true);
                await saveTask(task.parentId, saveParams);
                setUpdating(false);
            }
            setChanges({});
            onClose();
        },
        [saveTask, setChanges, onClose, task, changes, setUpdating, mode],
    );

    const handleDueDateChange = useCallback(
        (d: CalendarDateTime) => {
            if (d.value.hasTime) {
                setChanges({
                    ...changes,
                    dueDate: null,
                    dueDateTime: d.value.date,
                });
            } else {
                setChanges({
                    ...changes,
                    dueDate: d.value.date.toString(),
                    dueDateTime: null,
                });
            }
        },
        [changes, setChanges],
    );

    const dueDate: CalendarDate | null = useMemo(() => {
        if ("dueDate" in changes) {
            return CalendarDate.parse(changes.dueDate).getOrElse(null);
        }

        if (task.dueDate) {
            return CalendarDate.parse(task.dueDate).getOrElse(null);
        }

        return null;
    }, [changes.dueDate, task.dueDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const dueDateTime: Date | null = useMemo(() => {
        if ("dueDateTime" in changes) {
            return changes.dueDateTime ? new Date(changes.dueDateTime) : null;
        }

        if (task.dueDateTime) {
            return new Date(task.dueDateTime);
        }

        return null;
    }, [changes.dueDateTime, task.dueDateTime]); // eslint-disable-line react-hooks/exhaustive-deps

    const taskTypeId: TaskTypeId | null = useMemo(() => {
        if ("taskTypeId" in changes) {
            return changes.taskTypeId;
        }

        return task.taskTypeId;
    }, [changes.taskTypeId, task.taskTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTaskTypeChange = useCallback(
        (v: FilterableTaskTypes) => {
            setChanges({
                ...changes,
                taskTypeId:
                    v === unsetTaskTypeSentinel || v === allTaskTypesSentinel ? null : v,
            });
        },
        [changes, setChanges],
    );

    return (
        <form onSubmit={handleSave} className="space-y-4 mb-4">
            <Prompt
                when={isDirty}
                message="You have unsaved changes. Are you sure you want to discard them?"
            />
            <div>
                <span>Associated with</span>&nbsp;
                {entity ? (
                    props.entityLinkDisabled ? (
                        <span className="font-medium">{entity.displayName}</span>
                    ) : (
                        <Link className={defaultLinkStyle} to={`${entity.path}/tasks`}>
                            {entity.displayName}
                        </Link>
                    )
                ) : null}
            </div>
            <div className="space-y-1">
                <div className="space-y-1 lg:space-y-0 lg:flex lg:space-x-2 lg:items-center">
                    <div className="flex-1">
                        <Input
                            label="Title"
                            autoFocus
                            value={changes.text ?? task.text ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setChanges({ ...changes, text: e.target.value })
                            }
                        />
                    </div>
                    <div className="flex-1">
                        <TaskTypeSelect
                            value={taskTypeId ?? unsetTaskTypeSentinel}
                            onChange={handleTaskTypeChange}
                            allowUnsetTaskType
                            unsetLabel=""
                        />
                    </div>
                </div>
                <CalendarDateTimeInput
                    label="Due"
                    value={getCalendarDateTime(dueDate, dueDateTime)}
                    onChange={handleDueDateChange}
                />
                <UserSelect
                    includeEveryone={false}
                    includeUnassigned={false}
                    label="Assigned to"
                    value={changes.ownerId || task.ownerId}
                    onChange={v => setChanges({ ...changes, ownerId: v as UserId })}
                />
                <TextArea
                    label="Notes"
                    rows={3}
                    value={changes.notes ?? task.notes ?? ""}
                    onChange={e => setChanges({ ...changes, notes: e.target.value })}
                    autoGrow
                />
                <RecurrenceConfig
                    recurrencePattern={
                        changes.recurrencePattern ??
                        (task.recurrencePattern as RecurrencePattern)
                    }
                    recurrenceInterval={
                        changes.recurrenceInterval ?? task.recurrenceInterval
                    }
                    recurrenceDaysOfWeek={
                        (changes.recurrenceDaysOfWeek as DayOfWeek[]) ??
                        (task.recurrenceDaysOfWeek as DayOfWeek[])
                    }
                    recurrenceDayOfMonth={
                        changes.recurrenceDayOfMonth ?? task.recurrenceDayOfMonth
                    }
                    onChange={recurrenceChanges =>
                        setChanges({ ...changes, ...recurrenceChanges })
                    }
                />
            </div>
            <div className="flex items-center space-x-2">
                <PrimaryButton
                    onClick={handleSave}
                    disabled={updating || !task?.parentId}>
                    <span className="text-sm">Save</span>
                </PrimaryButton>
                <SecondaryButton onClick={handleCancel}>
                    <span className="text-sm">Cancel</span>
                </SecondaryButton>
                {updating ? <Spinner /> : null}
            </div>
        </form>
    );
};
